/**
 * buildArchetype — what SHAPE a team's money made, said as a label and nothing
 * more.
 *
 * 🔴 THE HARD SCOPE LINE, AND IT IS A PRODUCT DECISION RATHER THAN A GAP.
 * This module names builds. It does NOT say how a build performs — no rate of
 * anything, no placement, no claim about past seasons — and that silence holds
 * in the code, in the copy, and in these comments, which is why this paragraph
 * describes the forbidden claims rather than quoting one. The outcome layer is
 * deliberately withheld. buildArchetype.test.js greps this file, the chip and
 * the two views for that language and the grep has no exemptions, so a
 * demonstration of the banned phrasing would fail the suite exactly as the real
 * thing would. If you arrived here to add the outcome layer: the omission is
 * the feature, and the grep is not a formality standing in front of a gap.
 *
 * 🔴 EVERY MEASURED QUANTITY IS AN ABSOLUTE SHARE OF THE LEAGUE'S BUDGET, one
 * denominator, no exceptions. Not share of what a team SPENT — two teams that
 * bought the same players are the same build whether or not one of them left $6
 * on the table, and a spend denominator would make the label move when the
 * leftover moved. The point of a budget denominator is that two people with a
 * printout and a calculator get the same number, and neither of them needs this
 * file to do it.
 *
 * 🔴 THE CUT POINTS COME FROM THE CORPUS, AND THIS FILE CARRIES NONE OF ITS OWN
 * (changed Sep 3 2026). They used to be six hand-set constants shared by every
 * format, and the measured consequence is why they are gone: over the charted
 * cells' own 1,508 real teams, those constants labelled 68% of 1QB teams and
 * 37% of superflex teams Stars and scrubs. A label most teams wear describes
 * nothing. The cuts now live in rateBenchmarks.json's meta.archetypeCuts, built
 * by scripts/build_rate_benchmarks.py over the real TEAMS of each format's
 * charted cell, and what is FIXED is the percentile — "the top sixth of real
 * teams at this" — while the value follows the corpus and the format.
 *
 * It had to be per-format. 1QB teams are genuinely more top-heavy than
 * superflex teams (median top3Share 0.630 against 0.530) because a superflex
 * roster pays two started quarterbacks, which flattens its top three. One
 * shared number cannot be an edge in both. Evidence and the full measured
 * distributions: docs/data/archetype-cuts-sep03.md.
 *
 * 🔴 A LITERAL THRESHOLD IN THIS FILE IS A SECOND, SILENTLY STALE CORPUS. The
 * same trap roomSizeHeat.js documents for minRooms: a constant copied out of
 * the artifact stops moving when the artifact does, and nothing warns you.
 * Every cut is read through cutsFor() and nowhere else.
 *
 * THE RULES, in order, first match wins. Percentiles are of the room's own
 * format cell; the dollar-share values they land on are in the artifact:
 *
 *   STARS AND SCRUBS  top3Share >= p85  and  dollarOnePicks >= p75
 *   HERO RB           maxRBShare >= p85 and  secondRBShare <= p25
 *   ZERO RB           rbShare <= p10
 *   QB HEAVY          qbShare >= p85                  (QB-premium rooms only)
 *   BALANCED          everything else
 *
 * ORDER IS PART OF THE DEFINITION. A roster can satisfy two rules at once — a
 * hero back on a stars-and-scrubs team is the obvious one — and first-match
 * makes that deterministic instead of a matter of which check a future edit
 * happens to put first. Read the list as "the most particular claim that is
 * true of this roster".
 *
 * 🔴 BALANCED IS THE REMAINDER, AND IT IS THE HONEST BUCKET (merged Sep 3
 * 2026). There used to be a sixth label, SPREAD, for a roster that failed
 * BALANCED's own flatness tests as well as every distinctive rule. It was the
 * second-most-worn label in both formats and its entire content was "no rule
 * fired" — which is what a remainder bucket already says, in a word a reader
 * recognises. So BALANCED absorbed it and the flatness tests are gone with it:
 * the label now means exactly "none of the shapes above". Its evidence sentence
 * shows the general shape rather than a matched rule's numbers, which is what a
 * reader needs in order to check that nothing fired. On this corpus that is
 * 70% of superflex teams and 76% of 1QB teams, and a remainder carrying most of
 * the room is the truth about real auctions, not a classifier giving up.
 *
 * 🔴 THE DISPLAYED PERCENT IS ROUNDED AND THE THRESHOLD IS NOT. A team at 62.7%
 * renders "63%" and is measured at 0.627, which will look wrong to somebody
 * checking by hand exactly once. The alternative — testing the cut against the
 * rounded number — would make the label depend on the display, which is worse
 * and much harder to notice. The seam is documented here and lives nowhere
 * else, and it applies to the percentile clause too: that is read off the real
 * number, not off the rendered one.
 */

import benchmarks from "../data/rateBenchmarks.json";

/**
 * What counts as a pick out of the dollar bin, in the LEAGUE's own dollars.
 *
 * 🔴 NOT A CUT POINT — this defines the statistic, not the bar. Two dollars is
 * still the bin: plenty of rooms open bidding at $2, and a rule that missed
 * those would count the same roster differently for a house rule that has
 * nothing to do with the build. Its cut point (the 75th percentile of how many
 * such picks real teams make) is in the artifact like every other.
 *
 * 🔴 HAND-SYNC PACT: build_rate_benchmarks.py's team_shapes() applies the same
 * `<= 2` in league dollars, unscaling the corpus's $200 normalisation to do it.
 * Change one and the cuts are quantiles of a statistic the client no longer
 * computes. Change both in the same commit, and re-run the generator.
 */
export const DOLLAR_PICK_MAX = 2;

/** The label keys, and the words each one shows. */
export const BUILD_LABELS = {
  STARS_AND_SCRUBS: "Stars and scrubs",
  HERO_RB: "Hero RB",
  ZERO_RB: "Zero RB",
  QB_HEAVY: "QB heavy",
  BALANCED: "Balanced",
};

/** What a corpus cell is called out loud, for the evidence sentence. */
const FORMAT_WORDS = {
  superflex: "real superflex teams",
  "1qb": "real single QB teams",
};

/**
 * The cut points and the quantile grid for one room's format, or null.
 *
 * @param {string}  benchmarkFormat "superflex" | "1qb", the cell the room's
 *   prices are already graded in, so the label and the market numbers on the
 *   same page are measured against the same rooms.
 * @param {boolean} qbPremium the FALLBACK only, for a room whose format never
 *   made it this far (a hand-entered room with no priced seat to carry one).
 *   The two agree by construction — the corpus splits on the same predicate
 *   isQbPremiumFormat does — but relying on that coincidence instead of on the
 *   field that exists is how they would come to disagree.
 */
export function cutsFor(benchmarkFormat, qbPremium) {
  const key =
    benchmarkFormat && benchmarks[benchmarkFormat] ? benchmarkFormat : qbPremium ? "superflex" : "1qb";
  const cell = benchmarks[key];
  const cuts = cell && cell.meta && cell.meta.archetypeCuts;
  // Absent, never defaulted: an artifact with no cuts is not a licence to
  // invent six numbers, it is the reason there is no label to draw.
  if (!cuts || !cuts.cuts || !cuts.quantiles) return null;
  return { ...cuts, format: key, words: FORMAT_WORDS[key] || "real teams" };
}

/** One cut's dollar-share (or count) value, or null when the artifact lacks it. */
function cutValue(cuts, name) {
  const c = cuts && cuts.cuts && cuts.cuts[name];
  return c && Number.isFinite(c.value) ? c.value : null;
}

/**
 * How many of the format's real teams this number beats, rounded DOWN to the
 * artifact's grid — or, for `dir < 0`, how many it comes in under.
 *
 * 🔴 STRICTLY BELOW, AND NEVER INTERPOLATED. The sentence says "more than 85%
 * of real single QB teams", so the only honest reading is the largest grid step
 * whose quantile is strictly less than this team's own number. Strictly is what
 * makes ties safe: dollarOnePicks is a COUNT and its p70, p75 and p80 are all
 * the same integer on this corpus, so an "at or below" read would credit a team
 * standing on that integer with beating 80% of the room when the true figure is
 * nearer 62%. Interpolating between two equal quantiles produces a number with
 * no referent. Round down, under-claim.
 *
 * The low direction is the mirror: the smallest grid step whose quantile is
 * strictly greater, which bounds how many teams came in above this one.
 *
 * @returns {number|null} a whole percent, or null when the grid cannot bound it
 */
export function corpusPercentile(cuts, stat, value, dir = 1) {
  const row = cuts && cuts.quantiles && cuts.quantiles[stat];
  const grid = cuts && cuts.grid;
  if (!row || !grid || row.length !== grid.length || !Number.isFinite(value)) return null;
  if (dir > 0) {
    let best = null;
    for (let i = 0; i < grid.length; i += 1) if (row[i] < value) best = grid[i];
    return best === null || best === 0 ? null : best;
  }
  for (let i = 0; i < grid.length; i += 1) {
    if (row[i] > value) {
      const above = 100 - grid[i];
      return above === 0 ? null : above;
    }
  }
  return null;
}

/**
 * The measured shape of one roster, or null when there is nothing to measure.
 *
 * 🔴 NULL, NOT A ZEROED SHAPE, for an unpriced roster or a league with no
 * budget. A team typed without dollars has no build — it has a roster — and a
 * shape made of zeroes would classify every one of them as Balanced, which is a
 * label the data did not earn. The page's absent-not-defaulted rule, applied to
 * the one thing on the row that is a claim rather than a number.
 *
 * 🔴 MIRRORED IN build_rate_benchmarks.py's team_shapes(), quantity for
 * quantity. The cut points are quantiles OF these numbers; a statistic computed
 * one way here and another way there is a cut against a distribution nobody is
 * standing in.
 *
 * @param {Array}  picks  graded picks: { pos, paid }
 * @param {number} budget the league's own budget, the single denominator
 */
export function buildShape(picks, budget) {
  if (!Number.isFinite(budget) || budget <= 0) return null;
  const priced = (picks || []).filter((p) => {
    const paid = Number(p && p.paid);
    return Number.isFinite(paid) && paid > 0;
  });
  if (priced.length === 0) return null;

  const paidOf = (p) => Number(p.paid);
  const byPos = {};
  let dollarOnePicks = 0;
  for (const p of priced) {
    const pos = p.pos || "—";
    byPos[pos] = (byPos[pos] || 0) + paidOf(p);
    if (paidOf(p) <= DOLLAR_PICK_MAX) dollarOnePicks += 1;
  }

  const desc = priced.map(paidOf).sort((a, b) => b - a);
  const rbs = priced
    .filter((p) => p.pos === "RB")
    .map(paidOf)
    .sort((a, b) => b - a);

  const share = (v) => v / budget;
  const posShares = {};
  // Every position present, not only the six named ones: a roster that ever
  // carries a seventh gets its share counted rather than silently excused.
  for (const pos of Object.keys(byPos)) posShares[pos] = share(byPos[pos]);

  return {
    top3Share: share(desc.slice(0, 3).reduce((a, v) => a + v, 0)),
    maxRBShare: share(rbs[0] || 0),
    secondRBShare: share(rbs[1] || 0),
    rbShare: share(byPos.RB || 0),
    wrShare: share(byPos.WR || 0),
    qbShare: share(byPos.QB || 0),
    dollarOnePicks,
    posShares,
    maxPosShare: Object.values(posShares).reduce((a, v) => Math.max(a, v), 0),
    budget,
    pickCount: priced.length,
  };
}

/**
 * The label, from the shape and the format's own cuts. First match wins; the
 * order IS the definition.
 *
 * @param {object}  shape      from buildShape
 * @param {boolean} qbPremium  isQbPremiumFormat(positions) — superflex or 2QB
 * @param {object}  cuts       from cutsFor
 */
export function buildLabelKey(shape, qbPremium, cuts) {
  if (!shape || !cuts) return null;
  const cut = (name) => cutValue(cuts, name);
  const at = (name, value, dir) => {
    const v = cut(name);
    // A cut the artifact does not carry cannot fire. Missing is not "passes".
    if (v === null) return false;
    return dir > 0 ? value >= v : value <= v;
  };
  if (at("STARS_TOP3", shape.top3Share, 1) && at("STARS_DOLLAR_PICKS", shape.dollarOnePicks, 1))
    return "STARS_AND_SCRUBS";
  if (at("HERO_MAX_RB", shape.maxRBShare, 1) && at("HERO_SECOND_RB", shape.secondRBShare, -1))
    return "HERO_RB";
  if (at("ZERO_RB_MAX", shape.rbShare, -1)) return "ZERO_RB";
  // 🔴 GATED ON THE ROOM, NOT ON THE ROSTER. In a single-QB league the same
  // spend is one very expensive quarterback rather than a build, and calling it
  // QB heavy would name a strategy the format does not offer. The 1QB cell
  // carries a qbShare cut anyway — the artifact records what the corpus holds —
  // and this line is the one place that decides never to read it.
  if (qbPremium && at("QB_HEAVY", shape.qbShare, 1)) return "QB_HEAVY";
  return "BALANCED";
}

/** A share as a whole percent. See the rounding seam in this file's header. */
export function pct(share) {
  return `${Math.round(share * 100)}%`;
}

const WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

/** Small counts read as words, the way the sentence around them does. */
function count(n) {
  return n <= 10 ? WORDS[n] : String(n);
}

/**
 * The sentence under an opened team: the numbers THIS label was decided on, and
 * where those numbers sit among real teams.
 *
 * 🔴 IT SHOWS THE DECIDING NUMBERS, NOT A FIXED SET. The point of the sentence
 * is that a reader can re-run the rule themselves, so it names exactly the
 * quantities the matched rule reads and no others — a sentence that always
 * listed six shares would bury the two that mattered. BALANCED, which matched
 * no rule, shows the general shape instead, which is what a reader needs in
 * order to confirm that none of the rules fired.
 *
 * 🔴 AND IT SAYS WHAT THE NUMBER MEANS. "Top 3 players took 63% of the budget"
 * is a fact with no scale attached — 63% is only high if you already know what
 * real teams do, and the whole reason the old constants mislabelled two thirds
 * of a format is that nobody did. The clause carries the reference the label
 * was actually decided against, in the room's own format's words. It is dropped
 * rather than faked when the grid cannot bound the number: a team at the very
 * bottom of the distribution beats no measurable share of it, and "more than 0%
 * of real teams" is not a sentence.
 */
export function buildEvidence(key, shape, cuts) {
  if (!shape || !key) return null;
  const picks = (n) => `${count(n)} ${n === 1 ? "pick" : "picks"} at $1-2`;
  const words = (cuts && cuts.words) || "real teams";
  // "more than 85% of real single QB teams" / "less than 95% of ..." — one
  // clause, on the number the rule was decided by, or nothing at all.
  const vs = (stat, value, dir) => {
    const p = corpusPercentile(cuts, stat, value, dir);
    if (p === null) return "";
    return `, ${dir > 0 ? "more" : "less"} than ${p}% of ${words}`;
  };
  switch (key) {
    case "STARS_AND_SCRUBS":
      return `Top 3 players took ${pct(shape.top3Share)} of the budget${vs(
        "top3Share",
        shape.top3Share,
        1
      )}; ${picks(shape.dollarOnePicks)}.`;
    case "HERO_RB":
      return `The top back took ${pct(shape.maxRBShare)} of the budget${vs(
        "maxRBShare",
        shape.maxRBShare,
        1
      )}; the next one ${pct(shape.secondRBShare)}.`;
    case "ZERO_RB":
      return `RB took ${pct(shape.rbShare)} of the budget${vs(
        "rbShare",
        shape.rbShare,
        -1
      )}; WR ${pct(shape.wrShare)}.`;
    case "QB_HEAVY":
      return `QB took ${pct(shape.qbShare)} of the budget${vs("qbShare", shape.qbShare, 1)}.`;
    default:
      return `Top 3 players took ${pct(shape.top3Share)} of the budget${vs(
        "top3Share",
        shape.top3Share,
        1
      )}; RB ${pct(shape.rbShare)}, WR ${pct(shape.wrShare)}, QB ${pct(shape.qbShare)}.`;
  }
}

/**
 * Everything a row and its expansion need, or null when the team has no build
 * to name.
 *
 * @param {Array}   picks           graded picks: { pos, paid }
 * @param {number}  budget          the league's budget
 * @param {boolean} qbPremium       whether the ROOM forces QB demand past one starter
 * @param {boolean} hasPrices       false for a roster typed without dollars
 * @param {string}  benchmarkFormat the corpus cell this room is graded in
 */
export function buildArchetype({ picks, budget, qbPremium, hasPrices, benchmarkFormat }) {
  // Explicit false only, the same contract teamSpendBreakdown uses: a caller
  // with no opinion falls through to the shape test, which reaches the same
  // answer from the picks themselves.
  if (hasPrices === false) return null;
  const shape = buildShape(picks, budget);
  if (!shape) return null;
  const cuts = cutsFor(benchmarkFormat, !!qbPremium);
  const key = buildLabelKey(shape, !!qbPremium, cuts);
  if (!key) return null;
  return { key, label: BUILD_LABELS[key], shape, evidence: buildEvidence(key, shape, cuts) };
}
